// Forgot password? (FR-038; P-AUTH-1; SC-005): asks for an email and, for any well-formed address,
// says a link was sent if an account exists (the same for unknown addresses). Resend after 60 s and
// Back to Log in. The link is requested for this app (`client: 'app'`, in authApi). The email stays
// in memory only.
import { Link } from "expo-router";
import { useState } from "react";
import { StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { useCooldown } from "@/lib/useCooldown";
import { useSubmitAction } from "@/lib/useSubmitAction";
import { Button } from "@/ui/Button";
import { Form } from "@/ui/Form";
import { InlineMessage } from "@/ui/InlineMessage";
import { LinkButton } from "@/ui/LinkButton";
import { Text } from "@/ui/Text";
import { TextField } from "@/ui/TextField";

import { useAuthApi } from "../AuthApiContext";
import { AUTH_COPY } from "../copy";
import { fieldMessage, messageFor } from "../messages";
import { AuthCard } from "../ui/AuthCard";
import { checkEmail } from "../validation";

const RESEND_COOLDOWN_S = 60;

const useStyles = makeStyles((t) =>
  StyleSheet.create({ stack: { gap: t.space.lg } }),
);

function BackToLogIn() {
  return (
    <Link href="/login" asChild>
      <LinkButton role="link">{AUTH_COPY.actions.backToLogIn}</LinkButton>
    </Link>
  );
}

export function ForgotPasswordScreen() {
  const styles = useStyles();
  const authApi = useAuthApi();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const cooldown = useCooldown(RESEND_COOLDOWN_S);
  const request = useSubmitAction((address: string) =>
    authApi.requestPasswordReset(address),
  );

  async function send(address: string, again: boolean) {
    setProblem(null);
    const result = await request.submit(address);
    if (result.status === "done") {
      setSentTo(address);
      setResent(again);
      cooldown.start();
    } else if (result.status === "failed") {
      const view = messageFor(result.error, "requestPasswordReset");
      if (view.kind === "fields") setError(view.fields.email ?? null);
      else if (view.kind !== "linkInvalid") setProblem(view.text);
    }
  }

  function submit() {
    const e = checkEmail(email);
    if (!e.ok) {
      setError(fieldMessage("email", e.code));
      return;
    }
    setError(null);
    void send(e.value, false);
  }

  if (sentTo !== null) {
    return (
      <AuthCard
        title={AUTH_COPY.forgot.title}
        intro={AUTH_COPY.forgot.intro}
        testID="forgot-sent"
      >
        <View style={styles.stack}>
          <Text>{AUTH_COPY.forgot.sent(sentTo)}</Text>
          {resent ? (
            <InlineMessage tone="success">
              {AUTH_COPY.checkInbox.resent}
            </InlineMessage>
          ) : null}
          {problem ? (
            <InlineMessage tone="error">{problem}</InlineMessage>
          ) : null}
          <Button
            variant="outline"
            onPress={() => {
              if (!cooldown.active) void send(sentTo, true);
            }}
            pending={request.pending}
            disabled={cooldown.active}
            testID="forgot-resend"
          >
            {cooldown.active
              ? AUTH_COPY.actions.resendIn(cooldown.remaining)
              : AUTH_COPY.actions.resendEmail}
          </Button>
          <BackToLogIn />
        </View>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={AUTH_COPY.forgot.title}
      intro={AUTH_COPY.forgot.intro}
      testID="forgot-screen"
    >
      {problem ? <InlineMessage tone="error">{problem}</InlineMessage> : null}
      <Form onSubmit={submit} accessibilityLabel={AUTH_COPY.forgot.title}>
        <View style={styles.stack}>
          <TextField
            label={AUTH_COPY.fields.email}
            kind="email"
            value={email}
            onChangeText={setEmail}
            error={error}
            testID="forgot-email"
          />
          <Button
            onPress={submit}
            pending={request.pending}
            testID="forgot-submit"
          >
            {AUTH_COPY.actions.sendLink}
          </Button>
        </View>
      </Form>
      <BackToLogIn />
    </AuthCard>
  );
}
